"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, ArrowDown, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function MethodHeader() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.7,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  return <section id="header" data-nav="dark" className="relative min-h-[85vh] md:min-h-[90vh] w-full flex items-center justify-center overflow-hidden bg-secondary text-secondary-foreground">
      {/* Background Image with Dark Chiaroscuro Overlay */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/image.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/image.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/image.png-wvc-sizes" alt="Antique brass navigational compass and leather expedition journals" className="h-full w-full object-cover object-center scale-105 filter brightness-[0.45] contrast-110" loading="lazy" decoding="async" />
        {/* Subtle Dark Slate Vignette & Architectural Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/60 to-secondary/30 mix-blend-multiply" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-secondary/40 to-secondary/80" />
      </div>

      {/* Decorative Architectural Orbit Motif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center opacity-25">
        <div className="size-[520px] md:size-[780px] rounded-full border border-border/40" />
        <div className="absolute size-[380px] md:size-[560px] rounded-full border border-dashed border-border/30" />
        <div className="absolute size-[240px] md:size-[360px] rounded-full border border-border/20" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 py-24 md:py-32 text-center">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col items-center">
          {/* Eyebrow Label */}
          <motion.div variants={fadeInVariants} className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/40 bg-secondary/80 px-4 py-1.5 backdrop-blur-xs">
            <Compass className="size-3.5 text-primary-foreground stroke-[1.25]" />
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-primary-foreground font-semibold">
              Proprietary Methodology
            </span>
          </motion.div>

          {/* Sterling Architectural Rule */}
          <motion.div variants={fadeInVariants} className="mb-8 flex items-center gap-3 w-32 md:w-48 opacity-60">
            <div className="h-px flex-1 bg-border" />
            <div className="size-1.5 rotate-45 border border-border bg-transparent" />
            <div className="h-px flex-1 bg-border" />
          </motion.div>

          {/* Dictated Headline */}
          <motion.h1 variants={fadeInVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.18] tracking-tight text-secondary-foreground max-w-4xl">
            The Anchor & Orbit™ Method: Togetherness Without Forced Cohesion
          </motion.h1>

          {/* Editorial Subtitle */}
          <motion.p variants={fadeInVariants} className="mt-6 font-sans text-base sm:text-lg md:text-xl font-normal leading-relaxed text-secondary-foreground/80 max-w-2xl text-balance">
            A bespoke architectural framework designed for multi-generational families—uniting private individual freedom with unforgettable collective milestones.
          </motion.p>

          {/* Action CTAs */}
          <motion.div variants={fadeInVariants} className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-sans tracking-wider uppercase text-xs font-semibold px-8 py-6 rounded-xs shadow-md">
              <Link to="/AboutDiscoveryConsultationPage">
                <span>Plan Your Family Milestone</span>
                <Sparkles className="size-4 ml-1 opacity-90" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border/60 bg-transparent text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-sans tracking-wider uppercase text-xs font-medium px-8 py-6 rounded-xs">
              <Link to="#framework-foundations">
                <span>Explore The Architecture</span>
                <ArrowDown className="size-4 ml-1 opacity-80" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Inset Hairline Bottom Divider */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-border/40 z-10" />
    </section>;
}